import {
  Box,
  Button,
  HStack,
  Heading,
  IconButton,
  Input,
  PopoverBody,
  PopoverContent,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  ChakraStylesConfig,
  MultiValue,
  Select,
  SingleValue,
} from 'chakra-react-select';
import { debounce } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useTranslation } from 'react-i18next';
import { getPredicateLabels } from '../../../domain/dbid-predicates';
import {
  AggregatedOrderFilterType,
  ColumnType,
  OrderColumnDeepFragment,
  PredicateType,
} from '../../../gql/graphql';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { ResolvedOrder } from '../../../hooks/order';
import { formatQuantity } from '../../../utils/quantity-utils';
import { QuantityWithUnits } from '../../../domain/property-types';
import { columnIdToColumnName, columnTypeToId } from './utils';
import { isRegexValid } from 'src/utils/regex-valid';

type FilterPopoverProps = {
  resolvedOrders: ResolvedOrder[];
  orderColumns: OrderColumnDeepFragment[];
  isOpen: boolean;
  isUpdatingTable: boolean;
  defaultFilters?: AggregatedOrderFilterType[] | null;
  itemGroups: Record<string, Array<string>>;
  onFiltersChange: (
    filters: AggregatedOrderFilterType[]
  ) => Promise<void> | void;
  onClearFilter: () => Promise<void> | void;
  onUsingCascader?: (e: boolean) => void;
  columnNameMapping?: Record<string, { id: string; newName: string }>;
};

type FilterPopoverFormData = {
  filters: AggregatedOrderFilterType[];
};

const emptyQuery: AggregatedOrderFilterType = {
  columnName: null,
  columnType: null,
  predicate: null,
  predicateValues: [],
};

const NO_DISPLAY_VALUE = '<None>';

// Function is placed outside of component to prevent re-creation on every render.
// useCallback didn't like being used together with debounce.
const debouncedInputValue = debounce(
  (setValue, idx, value, validateFilters) => {
    setValue(`filters.${idx}.predicateValues.0`, value);
    validateFilters();
  },
  2000
);

export function FilterPopover({
  resolvedOrders,
  orderColumns,
  isOpen,
  defaultFilters,
  isUpdatingTable,
  itemGroups,
  onFiltersChange,
  onClearFilter,
  onUsingCascader,
  columnNameMapping,
}: FilterPopoverProps) {
  const { control, setValue, getValues } = useForm<FilterPopoverFormData>({
    defaultValues: { filters: [] },
  });
  const output = useWatch({
    name: 'filters',
    control,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'filters',
  });

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSubmittingFilter, setSubmittingFilter] = useState(false);
  const [isSubmittingClearFilter, setSubmittingClearFilter] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUpdatedTable, setHasUpdatedTable] = useState(false);
  const [activeFilters, setActiveFilters] = useState<
    AggregatedOrderFilterType[]
  >([]);
  const [activeFilterIndices, setActiveFilterIndices] = useState<number[]>([]);
  const [lastSubmittedFilters, setLastSubmittedFilters] = useState<
    AggregatedOrderFilterType[]
  >([]);

  const [activeInputValues, setActiveInputValues] = useState<string[]>([]);
  const [activeAnyOfOptions, setActiveAnyOfOptions] = useState<string[][]>([]);

  const { t } = useTranslation(['common', 'table']);

  const handleInputChange = (value: string, idx: number, debounce: boolean) => {
    // Update the input value in the input values array
    setActiveInputValues((prev) => {
      const newInputValues = [...prev];
      newInputValues[idx] = value;
      return newInputValues;
    });

    if (debounce) {
      debouncedInputValue(setValue, idx, value, validateFilters);
      return;
    }

    setValue(`filters.${idx}.predicateValues`, [value]);
    validateFilters();
  };

  const debouncedAnyOfValueRef = useRef(new Map());

  const getDebouncedUpdate = (selectId: number) => {
    if (!debouncedAnyOfValueRef.current.has(selectId)) {
      debouncedAnyOfValueRef.current.set(
        selectId,
        debounce((setValue, idx, values, validateFilters) => {
          setValue(`filters.${idx}.predicateValues`, values);
          validateFilters();
        }, 1000)
      );
    }
    return debouncedAnyOfValueRef.current.get(selectId);
  };

  const handleAnyOfChange = (values: string[], idx: number) => {
    setActiveAnyOfOptions((prev) => {
      const newActiveAnyOfOptions = [...prev];
      newActiveAnyOfOptions[idx] = values;
      return newActiveAnyOfOptions;
    });
    const debouncedUpdate = getDebouncedUpdate(idx);
    debouncedUpdate(setValue, idx, values, validateFilters);
  };

  // Hook for updating loading state
  useEffect(() => {
    if (!isSubmittingFilter && !isSubmittingClearFilter && hasUpdatedTable) {
      setIsLoading(false);
      setHasUpdatedTable(false);
    }
  }, [isSubmittingFilter, isSubmittingClearFilter, hasUpdatedTable]);

  // Hook for updating active filters
  useEffect(() => {
    setActiveFilters(
      activeFilterIndices.map(
        (index) =>
          ({
            columnName: output[index]?.columnName,
            columnType: output[index]?.columnType,
            predicate: output[index]?.predicate,
            predicateValues: output[index]?.predicateValues,
          } as AggregatedOrderFilterType)
      )
    );
  }, [activeFilterIndices, output]);

  const validateFilters = () => {
    const activeFilterIndices: number[] = [];
    const updatedInputValues: string[] = [];
    const updatedAnyOfOptions: string[][] = [];

    // eslint-disable-next-line complexity
    fields.forEach((_, index) => {
      const selectedColumn = getValues(`filters.${index}.columnName`);
      const selectedPredicate = getValues(`filters.${index}.predicate`);
      const selectedPredicateValues = getValues(
        `filters.${index}.predicateValues`
      );

      const hasSelectedColumn = !!selectedColumn;
      const hasSelectedPredicate = !!selectedPredicate;
      if (!selectedPredicateValues) return;
      const validRegex =
        selectedPredicate !== PredicateType.FromRegex ||
        isRegexValid(selectedPredicateValues[0] as string);
      const hasValidPredicateValues =
        selectedPredicate === PredicateType.Exists ||
        selectedPredicate === PredicateType.NotExists ||
        (!!selectedPredicateValues &&
          selectedPredicateValues.length > 0 &&
          selectedPredicateValues[0] !== '' &&
          validRegex);

      if (
        hasSelectedColumn &&
        hasSelectedPredicate &&
        hasValidPredicateValues
      ) {
        activeFilterIndices.push(index);
      }

      if (
        selectedPredicate === PredicateType.Contains ||
        selectedPredicate === PredicateType.NotContains ||
        selectedPredicate === PredicateType.GreaterThan ||
        selectedPredicate === PredicateType.LessThan ||
        selectedPredicate === PredicateType.FromRegex
      ) {
        // These predicates uses an input
        updatedInputValues.push(
          (selectedPredicateValues && selectedPredicateValues[0]) || ''
        );
        updatedAnyOfOptions.push([]);
      } else if (selectedPredicate === PredicateType.AnyOf) {
        // AnyOf uses a multi-select
        updatedInputValues.push('');
        updatedAnyOfOptions.push(selectedPredicateValues || []);
      } else {
        // All other predicates use a single select
        updatedInputValues.push('');
        updatedAnyOfOptions.push([]);
      }
    });

    setActiveInputValues(updatedInputValues);
    setActiveAnyOfOptions(updatedAnyOfOptions);
    setActiveFilterIndices(activeFilterIndices);
  };

  useEffect(() => {
    if (isOpen) {
      if (!isInitialLoad) {
        return;
      }
      if (defaultFilters && defaultFilters.length > 0) {
        replace(defaultFilters);

        // Set default values for cascader
        const defaultActiveIndices: number[] = [];
        const defaultInputValues: string[] = [];
        const defaultAnyOfOptions: string[][] = [];

        defaultFilters.forEach((value, index) => {
          const { columnName: selectedColumn } = value;
          if (selectedColumn) {
            defaultActiveIndices.push(index);
          }

          // Set the input values if the predicate is contains, does not contain, greater than or less than
          if (
            value.predicate === PredicateType.Contains ||
            value.predicate === PredicateType.NotContains ||
            value.predicate === PredicateType.GreaterThan ||
            value.predicate === PredicateType.LessThan ||
            value.predicate === PredicateType.FromRegex
          ) {
            defaultInputValues.push(
              value.predicateValues ? value.predicateValues[0] : ''
            );
            defaultAnyOfOptions.push([]);
          } else if (value.predicate === PredicateType.AnyOf) {
            defaultInputValues.push('');
            defaultAnyOfOptions.push(value.predicateValues || []);
          } else {
            defaultInputValues.push('');
            defaultAnyOfOptions.push([]);
          }
        });

        setActiveInputValues(defaultInputValues);
        setActiveAnyOfOptions(defaultAnyOfOptions);
        setActiveFilterIndices(defaultActiveIndices);
        setActiveFilters(defaultFilters);
        setLastSubmittedFilters(defaultFilters);
      } else {
        replace(emptyQuery);
        setActiveFilterIndices([]);
        setActiveFilters([]);
        setLastSubmittedFilters([]);
      }

      Promise.resolve().then(() => setIsInitialLoad(false));
    } else {
      setIsInitialLoad(true);
    }
  }, [replace, defaultFilters, isOpen, isInitialLoad]);

  const submitForm = useCallback(
    async (filters: AggregatedOrderFilterType[]) => {
      setIsLoading(true);
      try {
        await onFiltersChange(filters);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        setIsLoading(false);
      }
    },
    [onFiltersChange]
  );

  // Hook for determining if changes have been made
  useEffect(() => {
    if (isInitialLoad) {
      return;
    }
    if (activeFilters.length !== lastSubmittedFilters.length) {
      setChangesMade(true);
      return;
    }

    setChangesMade(
      JSON.stringify(activeFilters) !== JSON.stringify(lastSubmittedFilters)
    );
  }, [activeFilters, isInitialLoad, lastSubmittedFilters]);

  // Submit linking when active filters change
  useEffect(() => {
    if (changesMade && !isInitialLoad) {
      if (
        JSON.stringify(activeFilters) !== JSON.stringify(lastSubmittedFilters)
      ) {
        setChangesMade(false);

        if (activeFilters.length === 0) {
          // Clear if there are no active filters
          setSubmittingClearFilter(true);

          (onClearFilter() as Promise<void>).finally(() => {
            setSubmittingClearFilter(false);
          });

          setLastSubmittedFilters([]);
          return;
        }

        setSubmittingFilter(true);

        // Map quantities to geometric property set when submitting
        submitForm(activeFilters)
          .then(() => {
            setLastSubmittedFilters(activeFilters);
          })
          .finally(() => {
            setSubmittingFilter(false);
          });
      }
    }
  }, [
    activeFilters,
    changesMade,
    isInitialLoad,
    lastSubmittedFilters,
    onClearFilter,
    submitForm,
  ]);

  // Hook for making sure loading is set while table is updating
  useEffect(() => {
    if (isOpen) {
      if (isUpdatingTable) {
        setIsLoading(true);
      } else {
        setHasUpdatedTable(true);
      }
    }
  }, [isOpen, isUpdatingTable]);

  const dropdownValueColor = useColorModeValue('gray.800', 'gray.50');
  const activeDropdownValueColor = useColorModeValue('teal.500', 'teal.200');
  const dropdownIndicatorColor = useColorModeValue('gray.300', 'gray.500');
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const selectedItemTextColor = useColorModeValue('blue.700', 'blue.200');
  const selectedItemBackgroundColor = useColorModeValue(
    'blue.50',
    'rgba(66, 153, 225, 0.24)'
  );

  const chakraStyles: ChakraStylesConfig = {
    control: (provided, state) => {
      return {
        ...provided,
        borderRadius: 'base',
        minHeight: 6,
        maxHeight: 78,
        overflowY: 'auto',
        height: state.isMulti ? 'auto' : 6,
        cursor: 'pointer',
      };
    },
    valueContainer: (provided) => {
      return {
        ...provided,
        paddingLeft: 2,
        fontSize: 'xs',
        marginTop: -0.5,
        paddingRight: 0,
      };
    },
    input: (provided) => {
      return {
        ...provided,
        color: dropdownValueColor,
      };
    },
    multiValue: (provided) => {
      return {
        ...provided,
        fontSize: 'xs',
        paddingTop: 0.5,
        paddingBottom: 0,
        minHeight: 0,
        maxWidth: 200,
        minWidth: 'fit-content',
      };
    },
    multiValueRemove: (provided) => {
      return {
        ...provided,
        paddingY: 0,
      };
    },
    clearIndicator: (provided) => {
      return {
        ...provided,
        padding: 0,
        width: 4,
        height: 4,
        fontSize: '0.5rem',
        colorScheme: 'gray',
      };
    },
    dropdownIndicator: (provided) => {
      return {
        ...provided,
        marginX: 0,
        padding: 0,
        paddingBottom: 1,
        color: dropdownIndicatorColor,
        marginTop: '2px',
      };
    },
    noOptionsMessage: (provided) => {
      return {
        ...provided,
        fontSize: 'xs',
      };
    },
    menuList: (provided) => {
      return {
        ...provided,
        borderRadius: 'md',
        padding: 1,
      };
    },
    menu: (provided) => {
      return {
        ...provided,
        minWidth: 200,
      };
    },
    option: (provided, state) => {
      return {
        ...provided,
        fontSize: 'xs',
        borderRadius: 'lg',
        paddingY: 0,
        paddingLeft: 2,
        minHeight: 7,
        fontWeight: state.isSelected ? 'bold' : 'normal',
        color: state.isSelected ? selectedItemTextColor : undefined,
        backgroundColor: state.isSelected
          ? selectedItemBackgroundColor
          : undefined,
        _hover: {
          color: state.isSelected ? selectedItemTextColor : undefined,
          backgroundColor: state.isSelected
            ? selectedItemBackgroundColor
            : hoverBackgroundColor,
        },
      };
    },
  };

  const activeSelectChakraStyles: ChakraStylesConfig = {
    ...chakraStyles,
    valueContainer: (provided) => {
      return {
        ...provided,
        paddingLeft: 2,
        fontSize: 'xs',
        marginTop: -0.5,
        color: activeDropdownValueColor,
        paddingRight: 0,
      };
    },
  };

  // Helper function to get column display name
  const getColumnDisplayName = useCallback(
    (column: { id: string; name: string; columnType: ColumnType }) => {
      // If we have a mapping for this column, use the mapped name
      if (columnNameMapping && columnNameMapping[column.id]) {
        return columnNameMapping[column.id].newName;
      }
      return column.name;
    },
    [columnNameMapping]
  );

  // Define columns before getSelectValue that depends on it
  const columns = useMemo(() => {
    return orderColumns
      .map(({ id, name, columnType }) => ({
        id,
        name: getColumnDisplayName({ id, name, columnType }),
        originalName: name, // Keep track of the original name for filtering
        columnType,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [orderColumns, getColumnDisplayName]);

  // Now define getSelectValue that uses columns
  const getSelectValue = useCallback(
    (selectedValue?: Maybe<string>) => {
      if (!selectedValue) return null;

      // When reading the existing value, find the matching column by original name
      const matchingColumn = columns.find(
        (col) => col.originalName === selectedValue
      );
      if (matchingColumn) {
        // Found the column - create an option with the display name as label
        return {
          label: matchingColumn.name,
          value: `${matchingColumn.id}:${matchingColumn.originalName}`,
        };
      }

      // If no match found or it's a special formatted value, use as-is
      return { label: selectedValue, value: selectedValue };
    },
    [columns]
  );

  const getAnyOfOptions = useCallback(
    (columnName: string) => {
      // First check if this is a special column type by its name
      if (
        columnName ===
        columnIdToColumnName(columnTypeToId(ColumnType.ParentOrder))
      ) {
        return resolvedOrders.map((order) => order.name);
      } else if (
        columnName ===
        columnIdToColumnName(columnTypeToId(ColumnType.ParentOrderStatus))
      ) {
        return [...new Set(resolvedOrders.map((order) => order.status))];
      }

      /** TODO: If we have an order label and itemGroup that have the same name then they will be matched as order label since it's matched first
      thus might be more coherent to somehow include the columnType of the filter */

      // We need to determine whether the column we are looking at is an OrderLabelColumn.
      const orderLabelColumns = orderColumns.filter(
        ({ columnType }) => columnType === ColumnType.ParentOrderLabel
      );
      const isOrderLabelColumn = orderLabelColumns.find(
        (label) => label.name === columnName
      );
      if (isOrderLabelColumn) {
        // We need to return all possible order label values having this as the order label key
        const labels: { key: string; value: string }[] = resolvedOrders
          .flatMap((order: ResolvedOrder) => order.labels)
          .filter(({ key }) => key === columnName);
        return [...new Set(labels.map(({ value }) => value))];
      }

      const itemGroupColumns = orderColumns.filter(
        ({ columnType }) => columnType === ColumnType.ItemGroup
      );

      const itemGroupColumn = itemGroupColumns.find(
        (itemCol) => itemCol.name === columnName
      );

      if (itemGroupColumn) {
        return itemGroups[itemGroupColumn.name];
      }

      // For all other column types, find by original name
      const allColumnValues = resolvedOrders.flatMap((order) => {
        // Find the column in the order's columns by its name (not display name)
        const columnToCheck = order.orderColumns.find(
          (column) => column.name === columnName
        );

        if (columnToCheck?.columnType === ColumnType.Description) {
          return order.orderEntries.map((entry) => entry.name);
        } else if (columnToCheck?.columnType === ColumnType.Quantity) {
          return order.orderEntries.map((entry) =>
            entry.resolvedQuantity ? formatQuantity(entry.resolvedQuantity) : ''
          );
        } else if (columnToCheck?.columnType === ColumnType.Custom) {
          return order.orderEntries.flatMap((entry) =>
            entry.customFields.map((field) =>
              formatQuantity(field.resolvedValue as QuantityWithUnits)
            )
          );
        }

        return [];
      });

      // Return unique values
      return Array.from(new Set(allColumnValues));
    },
    [itemGroups, orderColumns, resolvedOrders]
  );

  return (
    <PopoverContent shadow={'lg'} minWidth={'3xs'} width={'fit-content'}>
      <PopoverBody>
        <Heading as={'h3'} size={'xs'} marginY={1}>
          {t('table:filter.title')}
        </Heading>
        <VStack gap={1}>
          <Text
            alignSelf={'flex-start'}
            fontSize={'xs'}
            marginLeft={1}
            marginTop={2}
            color="gray.500"
          >
            {t('table:filter.where')}
          </Text>
          {fields
            ? // eslint-disable-next-line complexity
              fields.map((field, idx) => {
                const selectedColumn = getValues(`filters.${idx}.columnName`);
                const selectedPredicate = getValues(`filters.${idx}.predicate`);
                const selectedPredicateValues = getValues(
                  `filters.${idx}.predicateValues`
                );

                const isActive = activeFilterIndices.includes(idx);

                return (
                  <React.Fragment key={field.id}>
                    <HStack align={'start'} width={'100%'}>
                      <Box maxWidth={200} width={'100%'} minWidth={144}>
                        <Controller
                          control={control}
                          name={`filters.${idx}.columnName`}
                          render={({ field: { onChange } }) => (
                            <Select
                              aria-label={`column select ${idx}`}
                              placeholder={t('table:filter.assign.placeholder')}
                              menuPlacement="auto"
                              isRequired
                              isDisabled={isLoading}
                              chakraStyles={
                                isActive
                                  ? activeSelectChakraStyles
                                  : chakraStyles
                              }
                              useBasicStyles
                              variant="filled"
                              options={columns.map((column) => ({
                                label: column.name,
                                // Store both id and originalName to use for filtering
                                value: `${column.id}:${column.originalName}`,
                              }))}
                              value={getSelectValue(selectedColumn)}
                              onChange={(selected) => {
                                const selectedVal = (
                                  selected as { value: string }
                                ).value;
                                // selectedVal is of the form `id:originalName`
                                const splitVal = selectedVal.split(':');
                                const colId = splitVal[0];
                                const originalName = splitVal[1] ?? selectedVal;
                                const columnType = orderColumns.find(
                                  (col) => col.id === colId
                                )?.columnType;

                                if (columnType) {
                                  setValue(
                                    `filters.${idx}.columnType`,
                                    columnType
                                  );
                                }
                                // Set the original column name for filtering
                                onChange(originalName);
                                validateFilters();
                              }}
                            />
                          )}
                        />
                      </Box>
                      {selectedColumn ? (
                        <Box minWidth={144}>
                          <Controller
                            control={control}
                            name={`filters.${idx}.predicate`}
                            render={({ field: { onChange } }) => (
                              <Select
                                aria-label={`predicate select ${idx}`}
                                menuPlacement="auto"
                                isRequired
                                isDisabled={isLoading}
                                chakraStyles={
                                  isActive
                                    ? activeSelectChakraStyles
                                    : chakraStyles
                                }
                                useBasicStyles
                                variant="filled"
                                options={Array.from(getPredicateLabels(t)).map(
                                  ([key, value]) => ({
                                    label: value,
                                    value: key,
                                  })
                                )}
                                value={
                                  selectedPredicate
                                    ? {
                                        label:
                                          getPredicateLabels(t).get(
                                            selectedPredicate
                                          ),
                                        value: selectedPredicate,
                                      }
                                    : null
                                }
                                onChange={(selected) => {
                                  onChange(
                                    (selected as { value: string })?.value
                                  );
                                  validateFilters();
                                }}
                              />
                            )}
                          />
                        </Box>
                      ) : undefined}

                      {selectedColumn &&
                      selectedPredicate &&
                      selectedPredicate !== PredicateType.Exists &&
                      selectedPredicate !== PredicateType.NotExists ? (
                        <Box flex={1} maxWidth={240} minWidth={100}>
                          {selectedPredicate === PredicateType.AnyOf ? (
                            <Controller
                              control={control}
                              name={`filters.${idx}.predicateValues`}
                              render={() => (
                                <Select
                                  aria-label={`predicate value select ${idx}`}
                                  menuPlacement="auto"
                                  isMulti
                                  closeMenuOnSelect={false}
                                  chakraStyles={
                                    isActive
                                      ? activeSelectChakraStyles
                                      : chakraStyles
                                  }
                                  variant="filled"
                                  tagVariant={isActive ? 'subtle' : 'solid'}
                                  isDisabled={isLoading}
                                  colorScheme={isActive ? 'teal' : undefined}
                                  useBasicStyles
                                  options={getAnyOfOptions(selectedColumn).map(
                                    (value) => ({
                                      label: value,
                                      value,
                                    })
                                  )}
                                  onChange={(selected) => {
                                    const values = (
                                      selected as MultiValue<{
                                        value: string;
                                      }>
                                    )?.map((e) => e.value);
                                    handleAnyOfChange(values, idx);
                                  }}
                                  value={activeAnyOfOptions[idx].map(
                                    (value) => ({
                                      label: value,
                                      value,
                                    })
                                  )}
                                />
                              )}
                            />
                          ) : selectedPredicate === PredicateType.Equals ||
                            selectedPredicate === PredicateType.NotEquals ? (
                            <Controller
                              control={control}
                              name={`filters.${idx}.predicateValues`}
                              render={({ field: { onChange } }) => (
                                <Select
                                  aria-label={`predicate value select ${idx}`}
                                  menuPlacement="auto"
                                  chakraStyles={
                                    isActive
                                      ? activeSelectChakraStyles
                                      : chakraStyles
                                  }
                                  variant="filled"
                                  tagVariant="solid"
                                  isDisabled={isLoading}
                                  useBasicStyles
                                  options={getAnyOfOptions(selectedColumn).map(
                                    (value) => ({
                                      label: value,
                                      value,
                                    })
                                  )}
                                  onChange={(selected) => {
                                    onChange([
                                      (
                                        selected as SingleValue<{
                                          value: string;
                                        }>
                                      )?.value,
                                    ]);
                                    validateFilters();
                                  }}
                                  value={
                                    selectedPredicateValues &&
                                    selectedPredicateValues.length > 0
                                      ? selectedPredicateValues
                                          .filter((e) => e !== '')
                                          .map((e) => ({ label: e, value: e }))
                                      : [undefined]
                                  }
                                />
                              )}
                            />
                          ) : (
                            <Input
                              aria-label={`predicate value input ${idx}`}
                              isRequired
                              isDisabled={isLoading}
                              size={'xs'}
                              height={6}
                              top={-0.5}
                              borderRadius={'base'}
                              variant={'filled'}
                              color={
                                isActive ? activeDropdownValueColor : undefined
                              }
                              onChange={(event) =>
                                handleInputChange(event.target.value, idx, true)
                              }
                              onBlur={(event) =>
                                handleInputChange(
                                  event.target.value,
                                  idx,
                                  false
                                )
                              }
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleInputChange(
                                    (event.target as HTMLInputElement).value,
                                    idx,
                                    false
                                  );
                                }
                              }}
                              value={activeInputValues[idx] ?? ''}
                            />
                          )}
                        </Box>
                      ) : (
                        <Box flex={1} />
                      )}

                      {(activeFilterIndices.length !== 0 || selectedColumn) && (
                        <Tooltip label="Remove filter">
                          <IconButton
                            size={'xs'}
                            colorScheme={'gray'}
                            variant={'ghost'}
                            isLoading={isLoading}
                            aria-label="remove filter"
                            icon={
                              <SparkelIcon icon={faTimes} color={'inherit'} />
                            }
                            onClick={() => {
                              if (fields.length === 1) {
                                append(emptyQuery);
                              }
                              remove(idx);
                              validateFilters();
                            }}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                    {idx !== output.length - 1 ? (
                      <Text
                        alignSelf={'flex-start'}
                        fontSize={'xs'}
                        marginLeft={1}
                        marginTop={2}
                        color="gray.500"
                      >
                        {t('table:filter.and')}
                      </Text>
                    ) : undefined}
                  </React.Fragment>
                );
              })
            : undefined}
        </VStack>
        {activeFilterIndices.length === fields.length && (
          <Button
            marginTop={2}
            leftIcon={<SparkelIcon icon={faPlus} color="inherit" />}
            size={'xs'}
            variant={'ghost'}
            onClick={() => {
              append(emptyQuery);
            }}
          >
            {t('table:filter.add-filter')}
          </Button>
        )}
      </PopoverBody>
    </PopoverContent>
  );
}
